import { DateRangePicker, InfiniteSelect, Input, Select, Slider, TreeSelect } from '@common-web/index.js';
import { Flex, Switch } from 'antd';
import { DATE_FORMATS, FILTER_TYPE } from '@constants/generic-constants.js';
import { formatOptionsForSelect } from '@utils/utils.js';
import { filterFieldFormat } from '../helper';
import CascadingDropdown from '../cascading-dropdown';

const RenderFilter = (props) => {
  const {
    filtersObject = {},
    setFiltersObject = () => { },
    item = {},
    handleFiltersChange = () => { },
    loading = false,
  } = props;

  const onSearch = (object = filtersObject, onEnter = false) => {
    !!onEnter && handleFiltersChange();
  };

  const handleSelectValueChange = ({ value = [], searchKey = '', mode = 'single', setAsSlug = false }) => {
    let filterValue = '';
    if (mode === 'multiple') {
      filterValue = value.map((item) => (setAsSlug ? item?.slug : item?.id))?.join(',');
    } else if (mode === 'single') {
      filterValue = setAsSlug ? value?.slug : value?.id;
    }
    setFiltersObject({ ...filtersObject, [searchKey]: `${filterValue}` });
  };

  const getFilter = () => {
    switch (item?.type) {
      case FILTER_TYPE.DROP_DOWN:
        const options = item?.nested ? item?.options?.flatMap((item) => item?.options) : item?.options;
        const selectedIds = filtersObject[item.key] ? filtersObject[item.key]?.split(',') : [];
        const selected = options?.filter((option) => {
          return selectedIds.find((selectedItem) => {
            if (
              item.setAsSlug
                ? selectedItem === option.slug
                : typeof option.id === 'number'
                  ? parseInt(selectedItem) === option.id
                  : selectedItem === option.id
            ) {
              return option;
            }
          });
        });
        return (
          <Select
            label={item?.label}
            required={true}
            labelSize={'small'}
            options={formatOptionsForSelect({ list: item.options, setAsSlug: item.setAsSlug })}
            value={formatOptionsForSelect({ list: selected })}
            placeholder={item.placeholder}
            allowClear={true}
            mode={item?.mode}
            loading={loading}
            onChange={(value) => {
              handleSelectValueChange({
                value,
                searchKey: item.key,
                mode: item?.mode,
                setAsSlug: item?.setAsSlug ? item?.setAsSlug : false,
              });
            }}
            onClear={() => {
              const newFiltersObject = { ...filtersObject };
              delete newFiltersObject[item.key];
              setFiltersObject(newFiltersObject);
              if (newFiltersObject) {
                onSearch(newFiltersObject);
              }
            }}
          />
        );
      case FILTER_TYPE.INFINITE_DROP_DOWN:
        return (
          <InfiniteSelect
            label={item?.label}
            required={true}
            labelSize={'small'}
            placeholder={item.placeholder}
            fetch={item.fetch}
            fetchKey={item.fetchKey}
            filterKey={item.key}
            queryKey={item?.queryKey}
            searchKey={item.searchKey}
            defaultValue={filtersObject[item.key]}
            mode={item?.mode ?? 'multiple'}
            loading={loading}
            customRender={item?.customRender}
            setter={item?.setter}
            onChange={(values) => {
              let filterValue;
              if (item?.mode === 'single') filterValue = item?.setAsSlug ? values.slug : values.id;
              else filterValue = values.map((item) => (item?.setAsSlug ? item?.slug : item?.slug)).join(',');
              setFiltersObject({ ...filtersObject, [item.key]: `${filterValue}` });
            }}
            setAsSlug={item?.setAsSlug}
            searchEnabled={item?.searchEnabled}
            capitalizeLabel={item?.capitalizeLabel}
          />
        );
      case FILTER_TYPE.RANGE:
        return (
          <Slider
            label={item?.label}
            required={true}
            filtersObject={filtersObject}
            filterKey={item?.key}
            loading={loading}
            placeholder={item.placeholder}
            isAdvancedFilter={item.isAdvancedFilter}
            applyOnlyLteAtMin={item?.applyOnlyLteAtMin}
            applyOnlyGteAtMax={item?.applyOnlyGteAtMax}
            onApply={(minValue, maxValue, options = {}) => {
              const obj = {
                ...filtersObject,
              };
              if (options.isAtMinRange && item?.applyOnlyLteAtMin) {
                delete obj[`f[${item.key}][gte]`];
                obj[`f[${item.key}][lte]`] = String(maxValue);
              }
              else if (options.isAtMaxRange && item?.applyOnlyGteAtMax) {
                delete obj[`f[${item.key}][lte]`];
                obj[`f[${item.key}][gte]`] = String(minValue);
              }
              else {
                obj[`f[${item.key}][gte]`] = String(minValue);
                obj[`f[${item.key}][lte]`] = String(maxValue);
              }
              obj.page && delete obj.page;
              setFiltersObject(obj);
              if (obj) {
                onSearch(obj);
              }
            }}
          />
        );
      case FILTER_TYPE.DATE:
        const picker = item?.pickerLevel ? item?.pickerLevel : 'date';
        return (
          <DateRangePicker
            label={item?.label}
            required={true}
            allowEmpty={[false, false]}
            placeholder={item?.placeholder}
            rangePresets={item?.rangePresets}
            showRangePreset={true}
            startDate={filtersObject[item?.keys ? `f[${item.keys[0]}][gte]` : `f[${item.key}][gte]`]}
            endDate={filtersObject[item?.keys ? `f[${item.keys[1]}][lte]` : `f[${item.key}][lte]`]}
            picker={picker}
            showTime={false}
            filterSelected={!!filtersObject?.[item?.keys ? `f[${item.keys[0]}][gte]` : `f[${item.key}][gte]`]}
            loading={loading}
            disableFutureDates={item?.disableFutureDates}
            onChange={(startDate, endDate) => {
              const obj = {
                ...filtersObject,
                ...(item?.keys
                  ? {
                    [`f[${item.keys[0]}][gte]`]: startDate?.startOf(picker).format(DATE_FORMATS.timePayloadFormat),
                    [`f[${item.keys[1]}][lte]`]: endDate?.endOf(picker).format(DATE_FORMATS.timePayloadFormat),
                  }
                  : {
                    [`f[${item.key}][gte]`]: startDate?.startOf(picker).format(DATE_FORMATS.timePayloadFormat),
                    [`f[${item.key}][lte]`]: endDate?.endOf(picker).format(DATE_FORMATS.timePayloadFormat),
                  }),
              };

              obj.page && delete obj.page;
              setFiltersObject(obj);
              if (startDate && endDate) {
                onSearch(obj);
              }
            }}
            onClear={() => {
              const newFiltersObject = { ...filtersObject };
              delete newFiltersObject[item?.keys ? `f[${item.keys[0]}][gte]` : `f[${item.key}][gte]`];
              delete newFiltersObject[item?.keys ? `f[${item.keys[1]}][lte]` : `f[${item.key}][lte]`];
              setFiltersObject(newFiltersObject);
              onSearch(newFiltersObject);
            }}
          />
        );
      case FILTER_TYPE.BOOLEAN_RANGE:
        const selectedValue = filtersObject[`f[${item.key}][gt]`]
          ? item.options.find(({ value }) => value === 'true')
          : filtersObject[`f[${item.key}][lte]`]
            ? item.options.find(({ value }) => value === 'false')
            : '';
        return (
          <Select
            label={item?.label}
            labelSize={'small'}
            required={true}
            value={selectedValue}
            options={formatOptionsForSelect({ list: item.options, setAsSlug: item.setAsSlug })}
            placeholder={item.placeholder}
            mode={'single'}
            loading={loading}
            allowClear={true}
            onClear={() => {
              const newFiltersObject = { ...filtersObject };
              delete newFiltersObject[`f[${item.key}][lte]`];
              delete newFiltersObject[`f[${item.key}][gt]`];
              setFiltersObject(newFiltersObject);
              if (newFiltersObject) {
                onSearch(newFiltersObject);
              }
            }}
            onChange={({ value }) => {
              const obj = {
                ...filtersObject,
              };
              if (value === 'true') {
                delete obj[`f[${item.key}][lte]`];
                delete filtersObject[`f[${item.key}][lte]`];
                obj[`f[${item.key}][gt]`] = '0';
              } else {
                delete obj[`f[${item.key}][gt]`];
                delete filtersObject[`f[${item.key}][gt]`];
                obj[`f[${item.key}][lte]`] = '0';
              }

              obj.page && delete obj.page;
              setFiltersObject(obj);
              if (obj) {
                onSearch(obj);
              }
            }}
          />
        );
      case FILTER_TYPE.HIDDEN:
        return null;
      default:
        return (
          <Input
            label={item?.label}
            required={true}
            labelSize={'small'}
            style={{ color: 'var(--ant-color-primary)', fontWeight: filtersObject[item.key] ? 'bold' : '' }}
            value={filtersObject[item.key] ? decodeURIComponent(filterFieldFormat(filtersObject[item.key || ''])) : ''}
            placeholder={item.placeholder}
            type={item?.inputType ?? 'text'}
            loading={loading}
            onChange={(e) => {
              filtersObject.page && delete filtersObject.page;
              setFiltersObject({
                ...filtersObject,
                [item.key]: e.target.value,
              });
            }}
            onPressEnter={() => {
              if (item?.toLowerCase) {
                const obj = {
                  ...filtersObject,
                  [item.key]:
                    item?.inputType === 'number' ? filtersObject[item.key] : filtersObject[item.key]?.toLowerCase(),
                };
                onSearch(obj, true);
              } else {
                onSearch({}, true);
              }
            }}
          />
        );
      case FILTER_TYPE.TREE_SELECT:
        // const treeSelectedIds = filtersObject[item.key] ? filtersObject[item.key]?.split(',')?.map(key => parseInt(key)) : [];
        const treeValue = filtersObject[item.key] ? parseInt(filtersObject[item.key]) : filtersObject[item.key];
        return (
          <TreeSelect
            label={item?.label}
            required={true}
            labelSize={'small'}
            value={treeValue}
            placeholder={item.placeholder}
            loading={loading}
            options={item.options}
            mode={item.mode}
            checkable={item.checkable}
            highlightSearch={item.highlightSearch}
            treeDefaultExpandedKeys={[`${treeValue}`]}
            onChange={(value, label, extra) => {
              const obj = {
                ...filtersObject,
                [item.key]: value,
                ...(item?.subKey ? { [item.subKey]: item?.optionsMap?.get(value)?.[item?.subKey] } : {}),
              };
              obj.page && delete obj.page;
              setFiltersObject(obj);
              if (obj) {
                onSearch(obj);
              }
            }}
          />
        );
      case FILTER_TYPE.TOGGLE:
        return (
          <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>{item?.label}</span>
            <Switch
              checked={filtersObject[item.key] === (item.onValue || 'true')}
              onChange={(checked) => {
                const obj = {
                  ...filtersObject,
                };
                if (checked) {
                  obj[item.key] = item.onValue || 'true';
                } else {
                  if (item.offValue) {
                    obj[item.key] = item.offValue;
                  } else {
                    delete obj[item.key];
                  }
                }
                obj.page && delete obj.page;
                setFiltersObject(obj);
                if (obj) {
                  onSearch(obj);
                }
              }}
            />
          </Flex>
        );
      case FILTER_TYPE.CASCADING_DROPDOWN:
        return (
          <CascadingDropdown
            item={item}
            filtersObject={filtersObject}
            setFiltersObject={setFiltersObject}
            loading={loading}
          />
        );
    }
  };

  return <>{getFilter()}</>;
};

export default RenderFilter;
